"use client";

import { useRef, useCallback, useEffect, useState } from "react";
import { LANDING_CONTAINER_CLASS } from "@/components/landing/constants";
import { Button, LoadingImage } from "@restomat/components/client/ui";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { motion, useInView } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import { fadeInUp, staggerContainer, cardReveal } from "./animations";
import { templates } from "./template-data";
import { cn } from "@restomat/shared-utils/utils";

const SCROLL_SPEED = 1;
const SCROLL_BACK_HOVER = 6;
const SCROLL_BACK_AUTO = 2;
const PAUSE_AT_EDGE_MS = 600;

function TemplateCard({
  id,
  titleKey,
  autoPlay = false,
}: {
  id: string;
  titleKey: string;
  autoPlay?: boolean;
}) {
  const t = useTranslations("landing.templateCarousel");
  const tTemplates = useTranslations("landing.templates");
  const title = tTemplates(`${titleKey}.title`);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const pauseRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (pauseRef.current) {
      clearTimeout(pauseRef.current);
      pauseRef.current = null;
    }
  }, []);

  // Auto-play loop (mobile active card only)
  useEffect(() => {
    if (!autoPlay) {
      cancel();
      return;
    }

    let direction: "down" | "up" = "down";

    const step = () => {
      const node = containerRef.current;
      if (!node) return;
      const max = node.scrollHeight - node.clientHeight;
      if (max <= 0) {
        animationRef.current = requestAnimationFrame(step);
        return;
      }
      if (direction === "down") {
        node.scrollTop += SCROLL_SPEED;
        if (node.scrollTop >= max) {
          node.scrollTop = max;
          pauseRef.current = setTimeout(() => {
            direction = "up";
            animationRef.current = requestAnimationFrame(step);
          }, PAUSE_AT_EDGE_MS);
          return;
        }
      } else {
        node.scrollTop -= SCROLL_BACK_AUTO;
        if (node.scrollTop <= 0) {
          node.scrollTop = 0;
          pauseRef.current = setTimeout(() => {
            direction = "down";
            animationRef.current = requestAnimationFrame(step);
          }, PAUSE_AT_EDGE_MS);
          return;
        }
      }
      animationRef.current = requestAnimationFrame(step);
    };

    animationRef.current = requestAnimationFrame(step);
    return cancel;
  }, [autoPlay, cancel]);

  // Desktop hover: scroll down while hovered, snap back on leave.
  // Disabled when autoPlay drives the scroll instead.
  const onMouseEnter = useCallback(() => {
    if (autoPlay) return;
    cancel();
    const scroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const max = el.scrollHeight - el.clientHeight;
      if (el.scrollTop < max) {
        el.scrollTop += SCROLL_SPEED;
        animationRef.current = requestAnimationFrame(scroll);
      }
    };
    animationRef.current = requestAnimationFrame(scroll);
  }, [autoPlay, cancel]);

  const onMouseLeave = useCallback(() => {
    if (autoPlay) return;
    cancel();
    const back = () => {
      const el = containerRef.current;
      if (!el) return;
      if (el.scrollTop > 0) {
        el.scrollTop = Math.max(0, el.scrollTop - SCROLL_BACK_HOVER);
        animationRef.current = requestAnimationFrame(back);
      }
    };
    animationRef.current = requestAnimationFrame(back);
  }, [autoPlay, cancel]);

  return (
    <Link
      href={`https://sablony.restomat.cz/${id}`}
      rel="noopener noreferrer"
      target="_blank"
      className="group block"
    >
      <div className="relative rounded-xl border-2 border-background/20 overflow-hidden transition-all duration-300 group-hover:border-secondary group-hover:shadow-lg group-hover:shadow-secondary/20">
        <div
          ref={containerRef}
          className="h-[20rem] md:h-[30rem] 2xl:h-[45rem] overflow-hidden"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <LoadingImage
            src={`/img/landing/templates/${id}.webp`}
            alt={t("templateAlt", { title })}
            width={640}
            height={1554}
            className="w-full h-auto"
          />
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-4 pt-12">
          <h3 className="font-primary text-base md:text-lg font-bold text-background">
            {title}
          </h3>
        </div>
      </div>
    </Link>
  );
}

function MobileCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<string>(templates[0].id);

  useEffect(() => {
    const root = trackRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        let best: IntersectionObserverEntry | null = null;
        for (const e of entries) {
          if (!best || e.intersectionRatio > best.intersectionRatio) {
            best = e;
          }
        }
        if (best && best.intersectionRatio > 0.7) {
          const id = (best.target as HTMLElement).dataset.tplId;
          if (id) setActiveId(id);
        }
      },
      { root, threshold: [0.5, 0.7, 0.9, 1] },
    );

    root
      .querySelectorAll<HTMLElement>("[data-tpl-id]")
      .forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <motion.div className="md:hidden -mx-4" variants={staggerContainer}>
      <div
        ref={trackRef}
        className="flex overflow-x-auto snap-x snap-mandatory gap-4 px-[15vw] pb-4"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {templates.map((template) => (
          <motion.div
            key={template.id}
            data-tpl-id={template.id}
            variants={cardReveal}
            className="snap-center shrink-0 w-[70vw] max-w-[20rem]"
          >
            <TemplateCard
              id={template.id}
              titleKey={template.titleKey}
              autoPlay={template.id === activeId}
            />
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

export default function TemplateCarousel() {
  const t = useTranslations("landing.templateCarousel");
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: "some" });
  const [expanded, setExpanded] = useState(false);

  return (
    <section
      ref={sectionRef}
      id="templates"
      className="border-b-4 border-black bg-foreground py-20 text-background"
    >
      <motion.div
        className={`${LANDING_CONTAINER_CLASS} space-y-10`}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={staggerContainer}
      >
        <motion.header
          className="flex flex-col gap-4 text-center"
          variants={fadeInUp}
        >
          <h2>{t("heading")}</h2>
          <p className="md:text-lg text-background/70">
            {t("subheading", { count: templates.length })}
          </p>
        </motion.header>

        {/* Mobile: horizontal scroll-snap carousel; active card auto-scrolls */}
        <MobileCarousel />

        {/* Desktop: collapsible grid; cards scroll on hover */}
        <motion.div
          className="hidden md:block relative"
          variants={staggerContainer}
        >
          <div
            className={cn(
              "grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6 overflow-hidden transition-[max-height] duration-500 ease-out",
              !expanded && "max-h-[34rem] 2xl:max-h-[49rem]",
            )}
          >
            {templates.map((template) => (
              <motion.div key={template.id} variants={cardReveal}>
                <TemplateCard id={template.id} titleKey={template.titleKey} />
              </motion.div>
            ))}
          </div>
          {!expanded && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-foreground via-foreground/85 to-transparent"
            />
          )}
        </motion.div>

        <motion.div className="flex justify-center" variants={fadeInUp}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
            className="hidden md:inline-flex gap-2 whitespace-nowrap"
          >
            {expanded ? (
              <>
                {t("hideTemplates")} <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                {t("showAllTemplates", { count: templates.length })}{" "}
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            asChild
            className="md:hidden"
          >
            <Link href="/sablony">{t("showAllTemplatesMobile")}</Link>
          </Button>
        </motion.div>
      </motion.div>
    </section>
  );
}
